import { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, ArrowUpRight, Download } from 'lucide-react';

export const TableSkeleton = ({ rows = 6, spans = [4, 2, 2, 2, 2] }) => (
  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden animate-pulse" data-testid="table-skeleton">
    <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100">
      {spans.map((s, i) => <div key={i} className={`col-span-${s} h-2.5 w-16 bg-slate-100 rounded`} />)}
    </div>
    <div className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="grid grid-cols-12 gap-3 px-4 py-3.5 items-center">
          {spans.map((s, i) => (
            <div key={i} className={`col-span-${s} h-3 bg-slate-100 rounded`} style={{ width: `${55 + ((r * 13 + i * 29) % 40)}%` }} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const useSort = (items, accessors, initial = { key: null, dir: 'asc' }) => {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    if (!sort.key || !accessors[sort.key]) return items;
    const get = accessors[sort.key];
    return [...items].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [items, sort, accessors]);
  const toggle = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  return { sorted, sort, toggle };
};

export const SortableHeader = ({ columns, sort, onSort, sticky = true }) => (
  <div className={`hidden sm:grid grid-cols-12 gap-3 px-4 py-2.5 bg-white/95 backdrop-blur-sm border-b border-slate-100 text-xs font-medium text-slate-500 rounded-t-[inherit] ${sticky ? 'sticky top-14 z-10' : ''}`}>
    {columns.map((c) => {
      const active = sort?.key === c.key;
      const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
      return c.sortable === false || !onSort ? (
        <div key={c.key} className={`col-span-${c.span} ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</div>
      ) : (
        <button
          key={c.key}
          type="button"
          onClick={() => onSort(c.key)}
          className={`col-span-${c.span} inline-flex items-center gap-1 text-left hover:text-slate-800 transition-colors group/sort ${active ? 'text-slate-800' : ''} ${c.align === 'right' ? 'justify-end' : ''}`}
          data-testid={`sort-${c.key}`}
        >
          {c.label}
          <Icon className={`w-3 h-3 transition-opacity ${active ? 'opacity-100 text-teal-600' : 'opacity-0 group-hover/sort:opacity-50'}`} />
        </button>
      );
    })}
  </div>
);

export const exportCsv = (filename, rows, columns) => {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map((c) => esc(c.label)).join(','), ...rows.map((r) => columns.map((c) => esc(c.value(r))).join(','))];
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const ExportButton = ({ onClick, disabled, testId = 'export-csv-btn' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="text-sm text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1.5 hover:underline disabled:opacity-40 disabled:no-underline"
    data-testid={testId}
  >
    <Download className="w-3.5 h-3.5" />
    Export CSV
  </button>
);

export const RowOpenButton = ({ onClick, testId, label = 'Open details' }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    onKeyDown={(e) => e.stopPropagation()}
    aria-label={label}
    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-slate-100/70 text-slate-400 hover:text-teal-700 hover:bg-teal-50 transition-colors group/open"
    data-testid={testId}
  >
    <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover/open:translate-x-px group-hover/open:-translate-y-px" />
  </button>
);
